import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /**
   * Badge content
   */
  children: ReactNode;

  /**
   * Color variant
   * @default 'default'
   */
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';

  /**
   * Badge size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Visual style
   * @default 'subtle'
   */
  appearance?: 'subtle' | 'solid' | 'outline';

  /**
   * Show dot indicator
   * @default false
   */
  dot?: boolean;

  /**
   * Show remove button
   * @default false
   */
  removable?: boolean;

  /**
   * Callback when remove button is clicked
   */
  onRemove?: () => void;

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Badge Component
 *
 * A small label component for displaying status, count, or category.
 * Supports multiple variants, sizes, and interactive features.
 *
 * @example
 * ```tsx
 * <Badge variant="success" dot>
 *   Active
 * </Badge>
 * ```
 */
export const Badge = ({
  children,
  variant = 'default',
  size = 'md',
  appearance = 'subtle',
  dot = false,
  removable = false,
  onRemove,
  className,
  ...props
}: BadgeProps) => {
  const baseStyles = 'inline-flex items-center gap-1 rounded-full font-medium transition-colors';

  const sizeStyles = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-0.5 text-sm',
    lg: 'px-3 py-1 text-base',
  };

  const variantStyles: Record<string, Record<string, string>> = {
    subtle: {
      default: 'bg-muted text-muted-foreground',
      primary: 'bg-primary/10 text-primary',
      secondary: 'bg-secondary/10 text-secondary',
      success: 'bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-900',
      warning: 'bg-yellow-50 dark:bg-yellow-950/20 text-yellow-700 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-900',
      error: 'bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-900',
      info: 'bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-900',
    },
    solid: {
      default: 'bg-foreground text-background',
      primary: 'bg-primary text-primary-foreground',
      secondary: 'bg-secondary text-secondary-foreground',
      success: 'bg-green-600 text-white',
      warning: 'bg-yellow-600 text-white',
      error: 'bg-red-600 text-white',
      info: 'bg-blue-600 text-white',
    },
    outline: {
      default: 'border border-border bg-transparent',
      primary: 'border border-primary text-primary bg-transparent',
      secondary: 'border border-secondary text-secondary bg-transparent',
      success: 'border border-green-600 text-green-600 bg-transparent',
      warning: 'border border-yellow-600 text-yellow-600 bg-transparent',
      error: 'border border-red-600 text-red-600 bg-transparent',
      info: 'border border-blue-600 text-blue-600 bg-transparent',
    },
  };

  // Safe access with explicit fallbacks
  const appearanceStyles = variantStyles[appearance] || variantStyles.subtle;
  const selectedVariantStyle = appearanceStyles[variant] || appearanceStyles.default || 'bg-muted text-muted-foreground';

  return (
    <span
      className={cn(
        baseStyles,
        sizeStyles[size],
        selectedVariantStyle,
        className
      )}
      {...props}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />}
      {children}
      {removable && onRemove && (
        <button
          type="button"
          className="ml-0.5 inline-flex h-3 w-3 items-center justify-center rounded-full hover:bg-black/10 dark:hover:bg-white/10"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label="Remove"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 3L3 9M3 3L9 9"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </span>
  );
};
